export type TimeEntryResultOutcome =
	| {kind: 'LOCKED_PERIOD'}
	| {kind: 'SAGE_ERROR'; errors: string[]}
	| {kind: 'PROGRAM_REVENUE_LOCKED'}
	| {kind: 'PARTIAL_SUCCESS'; succeededRows: number; failedRows: number}
	| {kind: 'TOTAL_FAILURE'}
	| {kind: 'SUCCESS'};

type TimeEntryMutationResultType =
	| {
			errors?: string[] | null;
			timeRegs?: unknown[] | null;
			timeReg?: {id?: string | null; node?: {id?: string | null} | null} | null;
	  }
	| null
	| undefined;

const isSageError = (errors: string[]): boolean =>
	errors.length > 0 && (errors[0].includes('SAGE ERROR') || errors[0].includes('Sage Intacct ERROR'));

/**
 * Classifies a createTimeReg/updateTimeReg/createMultipleTimeRegs mutation result into a
 * single outcome. createMultipleTimeRegs (PTO date range) can return a genuine partial
 * success — some days created, some not — so errors.length > 1 no longer means total
 * failure, and a per-day bulk error is formatted as "<date>: <code>" rather than the bare
 * code a single createTimeReg/updateTimeReg call throws, hence .includes() rather than
 * exact equality. timeReg is nested as timeReg.node.id (createTimeReg) or timeReg.id
 * (updateTimeReg).
 */
export const classifyTimeEntryResult = (resultType: TimeEntryMutationResultType): TimeEntryResultOutcome => {
	const errors = resultType?.errors || [];
	const succeededRows = resultType?.timeRegs?.length || (resultType?.timeReg?.node?.id || resultType?.timeReg?.id ? 1 : 0);
	const isTotalFailure = errors.length > 0 && succeededRows === 0;

	if (isTotalFailure && errors.length === 1 && errors[0].includes('TIME_REGISTRATION_IN_LOCKED_PERIOD')) {
		return {kind: 'LOCKED_PERIOD'};
	}
	if (isTotalFailure && isSageError(errors)) {
		return {kind: 'SAGE_ERROR', errors};
	}
	if (isTotalFailure && errors.length === 1 && errors[0].includes('PROGRAM_REVENUE_LOCKED')) {
		return {kind: 'PROGRAM_REVENUE_LOCKED'};
	}
	if (errors.length > 0 && succeededRows > 0) {
		return {kind: 'PARTIAL_SUCCESS', succeededRows, failedRows: errors.length};
	}
	if (errors.length > 0) {
		return {kind: 'TOTAL_FAILURE'};
	}
	return {kind: 'SUCCESS'};
};
